import { isFunction } from 'lodash';
import { useCallback } from 'react';
import { LocalDate } from '@js-joda/core';

import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';
import { AuthorizedPersonExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';

import { assertUnreachable, Bean, FieldControllerProps, useTranslate, ValidationRules } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { personalDataTranslationKeys } from '../../../translations';

type AuthorizedPersonIdentifier =
   | typeof Pesel.CLASS
   | typeof Passport.CLASS
   | typeof NationalIdentificationNumber.CLASS
   | 'BirthDate';

const AUTHORIZED_PERSON_IDENTIFIER_TYPES: AuthorizedPersonIdentifier[] = [
   'BirthDate',
   Pesel.CLASS,
   Passport.CLASS,
   NationalIdentificationNumber.CLASS,
];

export type AuthorizedPersonIdentifierType = typeof AUTHORIZED_PERSON_IDENTIFIER_TYPES[number];

export type AuthorizedPersonIdentifierTypeVisitor = <ACCEPT>(
   visitor:
      | ((type: AuthorizedPersonIdentifierType) => ACCEPT)
      | Record<`visit${AuthorizedPersonIdentifierType}`, () => ACCEPT>
) => ACCEPT;

export const createAuthorizedPersonTypeVisitor = (
   authorizedPersonIdentifierType: AuthorizedPersonIdentifierType
): AuthorizedPersonIdentifierTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(authorizedPersonIdentifierType);
   }

   switch (authorizedPersonIdentifierType) {
      case 'Pesel':
         return visitor.visitPesel();
      case 'Passport':
         return visitor.visitPassport();
      case 'NationalIdentificationNumber':
         return visitor.visitNationalIdentificationNumber();
      case 'BirthDate':
         return visitor.visitBirthDate();
      default:
         return assertUnreachable(authorizedPersonIdentifierType);
   }
};

export type AuthorizedPersonIdentifierVisitor = <ACCEPT>(visitor: {
   visitPesel: (pesel: Pesel) => ACCEPT;
   visitPassport: (passport: Passport) => ACCEPT;
   visitNationalIdentificationNumber: (nationalIdentificationNumber: NationalIdentificationNumber) => ACCEPT;
   visitBirthDate: (birthDate: LocalDate) => ACCEPT;
}) => ACCEPT;

export const authorizedPersonIdentifierVisitor = (
   externalIdentifier: AuthorizedPersonExternalIdentifier | undefined,
   birthDate: LocalDate | undefined
): AuthorizedPersonIdentifierVisitor => (visitor) => {
   if (birthDate) {
      return visitor.visitBirthDate(birthDate);
   }

   if (externalIdentifier) {
      return externalIdentifier.accept(visitor);
   }

   return assertUnreachable(undefined);
};

const VALIDATION_RULES: ValidationRules<Bean> = {
   required: true,
};

const useTriggerLabelExtractor = (): ((type?: AuthorizedPersonIdentifierType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(personalDataTranslationKeys.identifierType.typeName[type]) : '';
      },
      [translate]
   );
};

export type OnAuthorizedExternalIdentifierTypeSelect = (type: AuthorizedPersonIdentifierType | null) => void;

type AuthorizedPersonIdentifierTypeFieldProps = FieldControllerProps<
   'authorizedPersonIdentifierType',
   AuthorizedPersonIdentifierType
> & {
   readonly onSelect: OnAuthorizedExternalIdentifierTypeSelect;
};

export const AuthorizedPersonIdentifierTypeField = (props: AuthorizedPersonIdentifierTypeFieldProps): JSX.Element => {
   return (
      <Select<AuthorizedPersonIdentifierType>
         testId={'authorizedPersonIdentifierType'}
         label={personalDataTranslationKeys.identifierType.label}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         controller={props.register('authorizedPersonIdentifierType', VALIDATION_RULES)}
         defaultValue={'Pesel'}
         onSelect={props.onSelect}
      >
         <Select.List<AuthorizedPersonIdentifierType> items={AUTHORIZED_PERSON_IDENTIFIER_TYPES}>
            {(identifierType: AuthorizedPersonIdentifierType) => (
               <Select.Item
                  item={identifierType}
                  testId={identifierType}
                  label={personalDataTranslationKeys.identifierType.typeName[identifierType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
